import React, { useCallback, useContext, useEffect, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import { useHistory } from 'react-router-dom';

import { OrderShippingGrid } from '../components/shipping/OrderShippingGrid';
import { OrderScreen } from '../OrderScreen';
import {
    OrderScreenContext,
    CheckoutFlowState,
} from 'features/order-summary/screens/CheckoutScreen';
import {
    OrderEditAddressForm,
    ShippingDetailsRequest,
} from '../components/shipping/OrderEditAddressForm';

import {
    emptyFamilyAddress,
    FamilyAddress,
} from 'common/api/crm/family/address/models/FamilyAddress';
import { emptyAddress } from 'common/api/models/Address';
import { useHomeDetails } from 'common/features/store/duck/home/utils/useHomeDetails';
import { Btn } from 'components/btn/Btn';
import { Checkbox } from 'components/checkbox/Checkbox';
import { getPath, Routes } from 'routes';
import { OrderAddresses } from '../components/payment/OrderAddresses';
import { useDefaultAddress } from 'common/hooks/useDefaultAddress';
import { useEffectUntilDone } from 'hooks/useEffectUntilDone';
import { isInIframe } from 'utils';

export interface OrderShippingScreenProps {}

const OrderShippingScreenContent = () => {
    const { cartInfo, setCartInfo, cartInfoDetails, checkoutFlowState } =
        useContext(OrderScreenContext);
    const isLoading = cartInfoDetails.shipping.userAddresses.isLoading;
    const homeDetails = useHomeDetails();
    const [editingAddress, setEditingAddress] = useState<FamilyAddress>();
    const history = useHistory();
    const { shipToHome } = cartInfo;
    const disableCheckoutBtn = checkoutFlowState === CheckoutFlowState.DISABLED;
    const { defaultAddress, refreshDefaultAddress } = useDefaultAddress();
    const addresses = cartInfoDetails.shipping.userAddresses.addresses;

    const setEmptyEditingAddress = useCallback(() => {
        if (!homeDetails) {
            return;
        }
        setEditingAddress({
            ...emptyFamilyAddress,
            firstName: homeDetails.profile.firstName,
            lastName: homeDetails.profile.lastName,
        });
    }, [homeDetails, setEditingAddress]);

    useEffect(() => {
        if (!homeDetails) {
            return;
        }
        if (!isLoading && addresses && addresses.length <= 0) {
            setEmptyEditingAddress();
        }
    }, [isLoading, homeDetails, addresses, setEmptyEditingAddress]);

    const selectedAddress = cartInfoDetails.shipping.selectedAddress;

    useEffectUntilDone(
        (markDone: () => void) => {
            if (selectedAddress) {
                markDone();
                cartInfoDetails.shipping?.refreshShippingMethods?.(selectedAddress);
            }
        },
        [selectedAddress]
    );

    useEffect(() => {
        refreshDefaultAddress();
    }, [refreshDefaultAddress]);

    const request: ShippingDetailsRequest = {
        firstName: editingAddress?.firstName || '',
        lastName: editingAddress?.lastName || '',
        phoneNumber: editingAddress?.phoneNumber || '',
        ...(editingAddress || emptyAddress),
    };

    const onAddressSaved = (address: FamilyAddress) => {
        setCartInfo({
            ...cartInfo,
            shippingToAddressId: address.id,
        });
        setEditingAddress(undefined);

        cartInfoDetails.shipping.userAddresses.refreshAddresses();
        cartInfoDetails?.shipping?.refreshShippingMethods?.(address);
    };

    const renderEditAddressForm = () => {
        return (
            editingAddress && (
                <OrderEditAddressForm
                    addressId={editingAddress.id}
                    request={request}
                    onAddressSaved={onAddressSaved}
                    onCancel={() => setEditingAddress(undefined)}
                />
            )
        );
    };

    const confirmOrder = () => {
        history.push(Routes.ShopOrderReview, cartInfo);
    };

    const back = () => {
        const url = getPath(Routes.ShopOrderPayment, {
            shipToHome: shipToHome ? 'ship-to-home' : 'ship-to-coordinator',
        });
        history.push(url, cartInfo);
    };

    const onChangeSetShippingAddressAsDefault = (state: boolean) => {
        setCartInfo({
            ...cartInfo,
            setShippingAddressAsDefault: state,
        });
    };

    return (
        <>
            {!isLoading && (
                <div>
                    <div className="w-full max-w-xl">
                        {!editingAddress && (
                            <div className="mb-8">
                                <div className="text-brand-dark text-lg font-bold mb-1">
                                    Shipping Address
                                </div>
                                <OrderAddresses
                                    onEdit={(address) => setEditingAddress(address)}
                                    onChange={(address) => {
                                        cartInfoDetails.shipping?.refreshShippingMethods?.(address);
                                        setCartInfo({
                                            ...cartInfo,
                                            shippingToAddressId: address.id,
                                        });
                                    }}
                                    selectedAddressId={
                                        cartInfo.shippingToAddressId || defaultAddress?.id
                                    }
                                />
                                <a
                                    href="#"
                                    className="text-xs text-grey-1 cursor-pointer ml-auto"
                                    onClick={() => {
                                        setEmptyEditingAddress();
                                        return false;
                                    }}
                                >
                                    Add New Address
                                </a>
                            </div>
                        )}
                        {renderEditAddressForm()}
                        {!editingAddress && (
                            <>
                                <div className="mb-8">
                                    <Checkbox
                                        checked={Boolean(cartInfo.setShippingAddressAsDefault)}
                                        text="Make this my preferred shipping address"
                                        onChange={onChangeSetShippingAddressAsDefault}
                                    />
                                </div>
                                {selectedAddress && (
                                    <div className="mt-12 mb-14">
                                        <div className="text-brand-dark text-lg font-bold mb-1">
                                            Shipping Method
                                        </div>
                                        <OrderShippingGrid />
                                    </div>
                                )}

                                <div className="text-left">
                                    <div className="block">
                                        <div className="sm:hidden flex flex-col gap-4 justify-center w-full">
                                            <div className="text-center">
                                                <Btn
                                                    type="submit"
                                                    className="cart_review_order_button max-w-xs w-full"
                                                    onClick={confirmOrder}
                                                    disabled={
                                                        !cartInfo.shippingMethodId ||
                                                        disableCheckoutBtn
                                                    }
                                                >
                                                    Review Order
                                                </Btn>
                                            </div>
                                            <div className="text-center">
                                                <Btn
                                                    type="button"
                                                    className="max-w-xs w-full"
                                                    isSecondary={true}
                                                    onClick={back}
                                                >
                                                    Back
                                                </Btn>
                                            </div>
                                        </div>
                                        <div className="hidden sm:block text-left">
                                            <Btn
                                                type="button"
                                                className="mr-4"
                                                isSecondary={true}
                                                onClick={back}
                                            >
                                                Back
                                            </Btn>
                                            <Btn
                                                type="submit"
                                                className="cart_review_order_button hidden sm:inline-block"
                                                onClick={confirmOrder}
                                                disabled={
                                                    !cartInfo.shippingMethodId || disableCheckoutBtn
                                                }
                                            >
                                                Review Order
                                            </Btn>
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}
            {isLoading && (
                <>
                    <div>
                        <Skeleton
                            style={{
                                lineHeight: '2rem',
                                maxWidth: '30rem',
                                display: 'block',
                                marginBottom: '0.5rem',
                            }}
                        />
                        <Skeleton
                            style={{
                                lineHeight: '3rem',
                                maxWidth: '45rem',
                                display: 'block',
                                marginBottom: '0.5rem',
                            }}
                        />
                        <Skeleton
                            style={{
                                lineHeight: '3rem',
                                maxWidth: '45rem',
                                display: 'block',
                                marginBottom: '0.5rem',
                            }}
                        />
                    </div>

                    <div className="mt-8">
                        <Skeleton
                            style={{
                                lineHeight: '2rem',
                                maxWidth: '30rem',
                                display: 'block',
                                marginBottom: '0.5rem',
                            }}
                        />
                        <Skeleton
                            style={{
                                lineHeight: '3rem',
                                maxWidth: '45rem',
                                display: 'block',
                                marginBottom: '0.5rem',
                            }}
                        />
                        <Skeleton
                            style={{
                                lineHeight: '3rem',
                                maxWidth: '45rem',
                                display: 'block',
                                marginBottom: '0.5rem',
                            }}
                        />
                    </div>
                </>
            )}
        </>
    );
};

export const OrderShippingScreen = () => {
    const className = isInIframe() ? 'pt-20' : 'pt-0';
    return (
        <OrderScreen
            title="Shipping"
            orderStep="shipping"
            className={className}
            showSummary={true}
            showMobileSummary={true}
            embedView={true}
        >
            <OrderShippingScreenContent />
        </OrderScreen>
    );
};

export default OrderShippingScreen;
